/**
 * ReasonChipPicker primitive — Plan 02-10 / UI-SPEC §"<ReasonChipPicker>".
 *
 * Reused in approval-rejection sheet AND reward-rejection sheet (Plan 12).
 * Renders chips as Chip buttons (#155); selected chip uses solid surface,
 * unselected outlined glass. When the special 'Annet' chip is selected and
 * allowCustom=true, an 80-char text input is revealed for freeform reason.
 *
 * Contract:
 *   value     — currently-selected chip text OR the freeform string when
 *               'Annet' is active and the user has typed anything.
 *   onChange  — fires with the chip text on chip-tap; with the typed
 *               string on input change. Caller diffs against the chips
 *               array if it needs to know "is this freeform vs preset".
 *   chips     — list of chip texts. Convention: 'Annet' (Norwegian
 *               "Other") triggers the freeform branch when allowCustom.
 *   allowCustom — defaults to true. When false, 'Annet' behaves like any
 *                 other preset chip.
 */
import { type ReactNode } from 'react';
import { Chip } from './Chip';

interface ReasonChipPickerProps {
  value: string | null;
  onChange: (val: string) => void;
  chips: string[];
  allowCustom?: boolean;
  className?: string;
}

const OTHER_CHIP = 'Annet';

export function ReasonChipPicker({
  value,
  onChange,
  chips,
  allowCustom = true,
  className,
}: ReasonChipPickerProps): ReactNode {
  const otherInChips = chips.includes(OTHER_CHIP);
  // #155: fritekstgrenen er aktiv når «Annet» er valgt OG når verdien er
  // skrevet tekst (alt som ikke er en av de faste chipene). Før gjaldt bare
  // det første, så feltet forsvant ved første tastetrykk.
  const custom =
    allowCustom &&
    otherInChips &&
    value !== null &&
    (value === OTHER_CHIP || !chips.includes(value));

  return (
    <div
      data-testid="reason-chip-picker"
      className={['flex flex-col gap-2', className].filter(Boolean).join(' ')}
    >
      {/* #155: chipene er Chip-primitiven (40–44 px, fylt når valgt). */}
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => {
          const selected = chip === OTHER_CHIP && custom ? true : value === chip;
          return (
            <Chip
              key={chip}
              data-testid="reason-chip"
              data-selected={selected ? 'true' : 'false'}
              selected={selected}
              onClick={() => onChange(chip)}
            >
              {chip}
            </Chip>
          );
        })}
      </div>
      {custom ? (
        <input
          data-testid="reason-chip-other-input"
          type="text"
          maxLength={80}
          // Uten forhåndsutfylling: verdien 'Annet' betyr «Annet valgt, ingen
          // tekst ennå». Når noe skrives blir verdien teksten.
          defaultValue={value === OTHER_CHIP ? '' : (value ?? '')}
          onChange={(ev) => onChange(ev.target.value)}
          className="field"
        />
      ) : null}
    </div>
  );
}
